import { Link } from "react-router-dom";
import { Clock, ExternalLink, Mail, MapPin, Navigation, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatAddress, useSettings } from "@/context/SettingsContext";

export default function Contact() {
  const { settings } = useSettings();
  const address = formatAddress(settings);
  const mapQuery = encodeURIComponent(address || "Pune, Maharashtra");
  const embedSrc = settings.map_embed_url || `https://www.google.com/maps?q=${mapQuery}&output=embed`;
  const directionsUrl =
    settings.map_link || `https://www.google.com/maps/dir/?api=1&destination=${mapQuery}`;

  return (
    <div data-testid="contact-page">
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 md:py-24">
          <p className="text-sm tracking-[0.18em] uppercase text-sky-600">Visit Us</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 max-w-3xl leading-[1.05]">
            Easy to find, easy to reach
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-8 py-20 grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white border border-slate-100 rounded-2xl p-8 space-y-6">
            <div className="flex gap-4">
              <MapPin className="w-5 h-5 text-sky-500 mt-1 shrink-0" />
              <div>
                <p className="text-xs tracking-[0.16em] uppercase text-slate-500">Clinic address</p>
                <p className="mt-2 text-slate-800 leading-relaxed" data-testid="contact-address">
                  {address}
                </p>
                <a
                  href={directionsUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-directions-link"
                  className="mt-3 inline-flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700 transition-colors duration-200"
                >
                  <Navigation className="w-4 h-4" />
                  Get directions
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
            <div className="flex gap-4">
              <Phone className="w-5 h-5 text-sky-500 mt-1 shrink-0" />
              <div>
                <p className="text-xs tracking-[0.16em] uppercase text-slate-500">Phone</p>
                <a
                  href={`tel:${(settings.phone || "").replace(/\s/g, "")}`}
                  className="mt-2 block text-slate-800 hover:text-sky-600 transition-colors duration-200"
                  data-testid="contact-phone"
                >
                  {settings.phone}
                </a>
                {settings.whatsapp && (
                  <p className="text-sm text-slate-500 mt-1" data-testid="contact-whatsapp">
                    WhatsApp: {settings.whatsapp}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-4">
              <Mail className="w-5 h-5 text-sky-500 mt-1 shrink-0" />
              <div>
                <p className="text-xs tracking-[0.16em] uppercase text-slate-500">Email</p>
                <p className="mt-2 text-slate-800" data-testid="contact-email">
                  {settings.email}
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <Clock className="w-5 h-5 text-sky-500 mt-1 shrink-0" />
              <div>
                <p className="text-xs tracking-[0.16em] uppercase text-slate-500">Working hours</p>
                <p className="mt-2 text-slate-800" data-testid="contact-hours">
                  {settings.hours_weekday}
                </p>
                {settings.hours_weekend && (
                  <p className="text-sm text-slate-500 mt-1">{settings.hours_weekend}</p>
                )}
              </div>
            </div>
          </div>

          <Link to="/book">
            <Button
              className="w-full rounded-full h-12 bg-blue-900 text-white hover:bg-blue-950 hover:text-white"
              data-testid="contact-book-button"
            >
              Book Appointment
            </Button>
          </Link>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-2xl overflow-hidden border border-slate-100 h-full min-h-[420px] bg-slate-100">
            <iframe
              title="Nidaan Dental Care location"
              data-testid="contact-map"
              src={embedSrc}
              className="w-full h-full min-h-[420px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
